import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Home,
  Heart,
  EyeOff,
  Calendar,
  Bell,
  MessageSquare,
  User,
  Settings,
  HelpCircle,
  PlusCircle,
  List,
  Users,
  ClipboardList,
  BarChart3,
  Search,
  Flame,
  Eye,
  Mail,
  Webhook,
  Zap,
  BookOpen,
  
  MessageCircle,
  Shield,
  TrendingUp,
  Database,
  UserCheck,
  FileCheck,
  Calculator,
  LogOut,
  ChevronDown,
  MessageSquarePlus,
  Lightbulb,
  Moon,
  Sun,
  Palette,
  Target,
} from "lucide-react";
import { useTheme } from "next-themes";
import { NavLink } from "@/components/NavLink";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSprinter } from "@/hooks/useIsSprinter";
import { useNavigate } from "react-router-dom";
import privyLogomark from "@/assets/privy-logomark.png";
import { LogoSpin } from "@/components/LogoSpin";
import PrivyFeatureTour from "@/components/PrivyFeatureTour";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PREVIEW_ROLE_OPTIONS, previewRoleLabel } from "@/lib/previewRoles";

interface NavItem {
  title: string;
  url: string;
  icon: React.ElementType;
}

const agentNav: NavItem[] = [
  { title: "Dashboard", url: "/agent-dashboard", icon: LayoutDashboard },
  { title: "Listings", url: "/listings", icon: Home },
  { title: "My Listings", url: "/my-listings", icon: List },
  { title: "Add Listing", url: "/add-listing", icon: PlusCircle },
  { title: "Clients", url: "/agent-client-list", icon: Users },
  { title: "Showing Management", url: "/tour-management", icon: Calendar },
  { title: "Offers", url: "/offer-management", icon: ClipboardList },
  { title: "Inquiries", url: "/property-leads", icon: ClipboardList },
  { title: "My Team", url: "/my-team", icon: Users },
  { title: "Team Dashboard", url: "/team-dashboard", icon: BarChart3 },
  { title: "Team Chat", url: "/team-chat", icon: MessageCircle },
  { title: "Branding", url: "/branding", icon: Palette },
  { title: "Open Houses", url: "/open-house-calendar", icon: Calendar },
  { title: "Agent Lookup", url: "/agent-lookup", icon: Search },
];

const agentToolsNav: NavItem[] = [
  { title: "Quick CMA", url: "/quick-cma", icon: TrendingUp },
  { title: "Hot Sheets", url: "/hot-sheets", icon: Flame },
  { title: "Reverse Prospecting", url: "/reverse-prospecting", icon: Search },
  { title: "Market Stats", url: "/market-stats", icon: BarChart3 },
  { title: "Listing Performance", url: "/listing-performance", icon: BarChart3 },
  { title: "Listing Analytics", url: "/listing-analytics", icon: Eye },
  { title: "Seller Stats", url: "/seller-stats", icon: Users },
  { title: "Bring to Market", url: "/bring-to-market", icon: Home },
  { title: "Doc Requests", url: "/document-requests", icon: FileCheck },
];

const agentSettingsNav: NavItem[] = [
  { title: "Email Templates", url: "/email-templates", icon: Mail },
  { title: "Integrations", url: "/integrations", icon: Webhook },
  { title: "API Docs", url: "/api-documentation", icon: BookOpen },
  { title: "Data Import", url: "/data-import", icon: Database },
  { title: "Access Requests", url: "/access-requests", icon: UserCheck },
  { title: "Switch Requests", url: "/agent-switch-requests", icon: Users },
];

const clientNav: NavItem[] = [
  { title: "Dashboard", url: "/client-dashboard", icon: LayoutDashboard },
  { title: "Browse Listings", url: "/listings", icon: Home },
  { title: "Saved Listings", url: "/saved-listings", icon: Heart },
  
  { title: "My Tours", url: "/client-tours", icon: Calendar },
  { title: "My Agent", url: "/my-agent", icon: Users },
  { title: "My Profile", url: "/profile-settings", icon: User },
  { title: "Buy Box Alerts", url: "/buy-box", icon: Bell },
];

const adminNav: NavItem[] = [
  { title: "Admin Panel", url: "/admin", icon: Shield },
  { title: "Analytics", url: "/admin/analytics", icon: BarChart3 },
  { title: "Agent Approval", url: "/agent-approval", icon: UserCheck },
  { title: "Listing Moderation", url: "/listing-moderation", icon: FileCheck },
  { title: "Sprinters", url: "/admin/sprinters", icon: Zap },
  { title: "MLS Admin", url: "/mls-admin", icon: Database },
];

const sprinterEarlyAccessItem: NavItem = { title: "Sprinter Early Access", url: "/sprinter", icon: Zap };

const commonNav: NavItem[] = [
  { title: "Notifications", url: "/notifications", icon: Bell },
  { title: "Chat", url: "/chat", icon: MessageSquare },
  { title: "Mortgage Calculator", url: "/mortgage-calculator", icon: Calculator },
  { title: "Profile Settings", url: "/profile-settings", icon: Settings },
  { title: "Help", url: "/help", icon: HelpCircle },
];


function NavGroup({ label, items, onNavigate }: { label: string; items: NavItem[]; onNavigate?: () => void }) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton asChild tooltip={item.title}>
                <NavLink
                  to={item.url}
                  end
                  onClick={onNavigate}
                  className="hover:bg-sidebar-accent/50"
                  activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.title}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export function AppSidebar() {
  const { role, actualRole, user, signOut, viewAsRole, setViewAsRole } = useAuth();
  const { isSprinter } = useIsSprinter();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();
  const [focusMode, setFocusMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = localStorage.getItem("admin_focus_mode");
    // Default ON; only off if the user explicitly turned it off before.
    return stored === null ? true : stored === "1";
  });
  useEffect(() => {
    localStorage.setItem("admin_focus_mode", focusMode ? "1" : "0");
  }, [focusMode]);
  const isAdmin = actualRole === "admin";
  // While previewing another role, show that role's real navigation.
  const focusActive = isAdmin && focusMode && !viewAsRole;
  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const userInitial = user?.email?.charAt(0).toUpperCase() || "U";

  return (
    <>
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1">
          <LogoSpin src={privyLogomark} alt="Privy" className="h-7 w-7 shrink-0" />
          <span className="text-lg font-semibold text-sidebar-foreground group-data-[collapsible=icon]:hidden">
            Privy
          </span>
          {focusActive && (
            <span className="ml-auto text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/15 text-primary group-data-[collapsible=icon]:hidden">
              Focus
            </span>
          )}
        </div>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        {focusActive ? (
          <>
            <NavGroup
              label="Listings"
              items={[
                { title: "Listings", url: "/listings", icon: Home },
                { title: "My Listings", url: "/my-listings", icon: List },
                { title: "Add Listing", url: "/add-listing", icon: PlusCircle },
                { title: "Listing Moderation", url: "/listing-moderation", icon: FileCheck },
                ...(isSprinter ? [sprinterEarlyAccessItem] : []),
              ]}
              onNavigate={closeMobileSidebar}
            />
            <NavGroup
              label="Members"
              items={[
                { title: "Clients", url: "/agent-client-list", icon: Users },
                { title: "Agent Approval", url: "/agent-approval", icon: UserCheck },
                { title: "Access Requests", url: "/access-requests", icon: UserCheck },
                { title: "Switch Requests", url: "/agent-switch-requests", icon: Users },
              ]}
              onNavigate={closeMobileSidebar}
            />
            <NavGroup
              label="Sprinters"
              items={[
                { title: "Sprinters", url: "/admin/sprinters", icon: Zap },
              ]}
              onNavigate={closeMobileSidebar}
            />
            <NavGroup
              label="Administration"
              items={[
                { title: "Admin Panel", url: "/admin", icon: Shield },
                { title: "Analytics", url: "/admin/analytics", icon: BarChart3 },
                { title: "MLS Admin", url: "/mls-admin", icon: Database },
              ]}
              onNavigate={closeMobileSidebar}
            />
            <NavGroup
              label="General"
              items={[
                { title: "Notifications", url: "/notifications", icon: Bell },
                { title: "Profile Settings", url: "/profile-settings", icon: Settings },
                { title: "Help", url: "/help", icon: HelpCircle },
              ]}
              onNavigate={closeMobileSidebar}
            />
          </>
        ) : (
          <>
            {(role === "agent" || role === "admin") && (
              <>
                <NavGroup
                  label="Agent"
                  items={isSprinter ? [
                    ...agentNav.slice(0, 4),
                    sprinterEarlyAccessItem,
                    ...agentNav.slice(4),
                  ] : agentNav}
                  onNavigate={closeMobileSidebar}
                />
                <NavGroup label="Tools" items={agentToolsNav} onNavigate={closeMobileSidebar} />
                <NavGroup label="Settings & Integrations" items={agentSettingsNav} onNavigate={closeMobileSidebar} />
              </>
            )}
            {(role === "client" || role === "co_buyer") && (
              <NavGroup
                label="My Home Search"
                items={isSprinter ? [
                  ...clientNav.slice(0, 2),
                  sprinterEarlyAccessItem,
                  ...clientNav.slice(2),
                ] : clientNav}
                onNavigate={closeMobileSidebar}
              />
            )}
            {(role === "admin" || role === "mls_admin") && (
              <NavGroup label="Administration" items={adminNav} onNavigate={closeMobileSidebar} />
            )}
            <NavGroup label="General" items={commonNav} onNavigate={closeMobileSidebar} />
          </>
        )}

      </SidebarContent>

      <SidebarSeparator />

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
              tooltip={resolvedTheme === "dark" ? "Light mode" : "Dark mode"}
              className="hover:bg-sidebar-accent/50"
            >
              {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{resolvedTheme === "dark" ? "Light mode" : "Dark mode"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setTourOpen(true)}
              tooltip="How It Works"
              className="hover:bg-sidebar-accent/50"
            >
              <Lightbulb className="h-4 w-4" />
              <span>How It Works</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setFeedbackOpen(true)}
              tooltip="Feedback"
              className="hover:bg-sidebar-accent/50"
            >
              <MessageSquarePlus className="h-4 w-4" />
              <span>Feedback</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {isAdmin && !viewAsRole && (
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => setFocusMode((v) => !v)}
                tooltip={focusMode ? "Exit Focus Mode" : "Enter Focus Mode (admin-only nav)"}
                className="hover:bg-sidebar-accent/50"
              >
                <Target className="h-4 w-4" />
                <span>{focusMode ? "Exit Focus Mode" : "Focus Mode"}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {actualRole === "admin" && (
            <>
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      tooltip="Preview the app as another type of user"
                      className="text-sidebar-foreground hover:bg-accent/50"
                    >
                      <Eye className="h-4 w-4" />
                      <span>
                        {viewAsRole ? `Previewing: ${previewRoleLabel(viewAsRole)}` : "Preview As..."}
                      </span>
                      <ChevronDown className="ml-auto h-4 w-4 opacity-60" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="top" align="start" className="w-60 bg-popover z-50">
                    <DropdownMenuLabel>Preview as</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {PREVIEW_ROLE_OPTIONS.map((opt) => (
                      <DropdownMenuCheckboxItem
                        key={opt.role}
                        checked={viewAsRole === opt.role}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setViewAsRole(opt.role);
                            navigate(opt.home);
                          } else {
                            setViewAsRole(null);
                            navigate("/admin");
                          }
                          closeMobileSidebar();
                        }}
                      >
                        <div className="flex flex-col">
                          <span>{opt.label}</span>
                          <span className="text-xs text-muted-foreground">{opt.description}</span>
                        </div>
                      </DropdownMenuCheckboxItem>
                    ))}
                    {viewAsRole && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => {
                            setViewAsRole(null);
                            navigate("/admin");
                            closeMobileSidebar();
                          }}
                        >
                          Exit preview (back to Admin)
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </>
          )}

          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleSignOut}
              tooltip="Sign Out"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="flex items-center gap-2 px-2 py-1 group-data-[collapsible=icon]:justify-center">
          <Avatar className="h-7 w-7">
            <AvatarFallback className="text-xs bg-primary text-primary-foreground">
              {userInitial}
            </AvatarFallback>
          </Avatar>
          <span className="text-xs text-muted-foreground truncate group-data-[collapsible=icon]:hidden">
            {user?.email}
          </span>
        </div>
      </SidebarFooter>
    </Sidebar>
    <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    <PrivyFeatureTour open={tourOpen} onOpenChange={setTourOpen} role={role} />
    </>
  );
}
